import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getAffinityInsights } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';

const TIER_COLORS = {
  standard:  { bg: '#f0f0f0', color: '#555' },
  premium:   { bg: '#fef9e7', color: '#d4ac0d' },
  exclusive: { bg: '#fdf0f0', color: '#e74c3c' },
};

const AffinityInsightsPage = () => {
  const { isMobile } = useWindowSize();
  const [insights, setInsights] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [filter,   setFilter]   = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await getAffinityInsights();
        setInsights(res.data.data?.insights || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load affinity data. Please refresh.');
      } finally { setLoading(false); }
    })();
  }, []);

  const healerOptions = insights.map(h => ({ value: h.healerId, label: h.healerName }));
  const displayed     = filter === 'all' ? insights : insights.filter(h => h.healerId === filter);

  return (
    <div style={s.layout}>
      <Sidebar />
      <div style={{ ...s.main, marginLeft: isMobile ? 0 : '260px', padding: isMobile ? '56px 16px 16px' : '32px' }}>

        <div style={s.header}>
          <div>
            <h1 style={s.title}>Healer Affinity Insights</h1>
            <p style={s.sub}>Outcome signals by condition — data builds as cases complete</p>
          </div>
          {healerOptions.length > 0 && (
            <select style={s.filterSelect} value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="all">All Healers</option>
              {healerOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
        </div>

        {loading ? (
          <div style={s.empty}>Loading affinity data…</div>
        ) : error ? (
          <div style={{ ...s.empty, color: '#e74c3c' }}>{error}</div>
        ) : displayed.length === 0 ? (
          <div style={s.empty}>No affinity data yet — complete healing cases to build signals.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {displayed.map(h => {
              const tier = h.healerTier || 'standard';
              const tc   = TIER_COLORS[tier] || TIER_COLORS.standard;
              return (
                <div key={h.healerId} style={s.card}>
                  <div style={s.cardHeader}>
                    <div style={s.healerName}>{h.healerName}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ ...s.tierBadge, background: tc.bg, color: tc.color }}>
                        {tier.toUpperCase()}
                      </span>
                      <span style={s.casesLabel}>{h.totalCases} total cases</span>
                    </div>
                  </div>
                  {h.affinities.length === 0 ? (
                    <div style={s.noData}>No condition data recorded yet.</div>
                  ) : (
                    <table style={s.table}>
                      <thead>
                        <tr style={s.tableHead}>
                          <th style={s.th}>Condition</th>
                          <th style={{ ...s.th, textAlign: 'right' }}>Cases</th>
                          <th style={{ ...s.th, textAlign: 'right' }}>Positive</th>
                          <th style={{ ...s.th, width: '160px' }}>Affinity Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {h.affinities.map(a => (
                          <tr key={a.condition} style={s.tableRow}>
                            <td style={s.td}>
                              <span style={s.condition}>{a.condition}</span>
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>{a.casesCompleted}</td>
                            <td style={{ ...s.td, textAlign: 'right' }}>{a.positiveOutcomes}</td>
                            <td style={s.td}>
                              {a.qualified ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={s.barTrack}>
                                    <div style={{
                                      ...s.barFill,
                                      width: `${a.affinityScore}%`,
                                      background: a.affinityScore >= 70 ? '#27ae60' : a.affinityScore >= 40 ? '#d4ac0d' : '#e74c3c',
                                    }} />
                                  </div>
                                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e', minWidth: '36px' }}>
                                    {a.affinityScore}%
                                  </span>
                                </div>
                              ) : (
                                <span style={s.coldStart}>Not enough data (&lt;5 cases)</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  layout:       { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  main:         { flex: 1 },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' },
  title:        { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
  sub:          { fontSize: '13px', color: '#666', margin: 0 },
  filterSelect: { padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', fontFamily: "'Open Sans', sans-serif", background: '#fff', color: '#333' },
  empty:        { textAlign: 'center', padding: '60px', color: '#aaa', fontSize: '14px' },
  card:         { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  cardHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e8f0e8', background: '#f9faf7', flexWrap: 'wrap', gap: '8px' },
  healerName:   { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e' },
  tierBadge:    { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '1px', padding: '3px 10px', borderRadius: '20px' },
  casesLabel:   { fontSize: '12px', color: '#999' },
  noData:       { padding: '20px 24px', fontSize: '13px', color: '#aaa', fontStyle: 'italic' },
  table:        { width: '100%', borderCollapse: 'collapse' },
  tableHead:    { background: '#f9faf7' },
  th:           { padding: '10px 16px', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#666', fontFamily: "'Raleway', sans-serif", borderBottom: '1px solid #e0e0e0', textAlign: 'left' },
  tableRow:     { borderBottom: '1px solid #f5f5f5' },
  td:           { padding: '12px 16px', fontSize: '13px', color: '#333', verticalAlign: 'middle' },
  condition:    { fontFamily: "'Raleway', sans-serif", fontWeight: '600', color: '#1a1a2e', textTransform: 'capitalize' },
  barTrack:     { width: '90px', height: '8px', background: '#eee', borderRadius: '4px', overflow: 'hidden' },
  barFill:      { height: '100%', borderRadius: '4px', transition: 'width 0.3s' },
  coldStart:    { fontSize: '11px', color: '#bbb', fontStyle: 'italic' },
};

export default AffinityInsightsPage;
